import React, { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TextLink from 'LuxKit/TextLink'
import ModalContext from 'contexts/ModalContext'
import CruiseModalItineraryDetails from 'components/Cruises/CruiseTiles/Common/CruiseModalItineraryDetails'
import CruiseModalPrices from 'components/Cruises/CruiseTiles/Common/CruiseModalPrices/CruiseModalPrices'
import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'
interface Props {
  offer: App.CruiseOffer | App.TourOffer | App.TourOfferSummary
  variant?: 'defualt' | 'condensed'
}

function CruiseDescription({ offer, variant }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const departures = 'departuresSummary' in offer ? offer.departuresSummary : undefined

  const {
    earliestDepartureDate,
    itinerary,
    itineraryFlash,
    isFlashOffer,
    itineraryDetailsLabel,
    laterDates,
    cruiseLineName,
    shipName,
    offerTitle,
  } = useCruiseOfferView({ offer })

  const openItineraryModal = useCallback(async(e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (isFlashOffer) {
      await showModal<boolean>(<CruiseModalItineraryDetails itineraryFlash={itineraryFlash} offerTitle={offerTitle} offer={offer} />)
    } else if (itinerary) {
      await showModal<boolean>(<CruiseModalItineraryDetails itinerary={itinerary} offerTitle={offerTitle} offer={offer} />)
    }
  }, [isFlashOffer, itinerary, showModal, itineraryFlash, offerTitle, offer])

  const openPricesModal = useCallback(async(e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (!isFlashOffer) {
      await showModal<boolean>(<CruiseModalPrices
        offerTitle={offerTitle}
        offer={offer}
        departures={departures}
      />)
    }
  }, [departures, isFlashOffer, offer, offerTitle, showModal])

  if (variant === 'condensed') {
    return <BodyText variant="medium" colour="neutral-two">
      {itineraryDetailsLabel}
    </BodyText>
  }

  return (
    <Group direction="vertical" gap={8}>
      <BodyText variant="medium" colour="neutral-two">
        {intl.formatMessage({
          id: 'dBiRGGNxcf92QhaZChKH',
          defaultMessage: '{cruiseLineName} {separator} {shipName}',
          description: 'Cruise line name and ship name separated by middle dot',
        }, { cruiseLineName, separator: intl.formatMessage(middleDotSeparatorMessage), shipName })}
      </BodyText>
      <Group direction="horizontal" gap={8}>
        <BodyText variant="medium" colour="neutral-two">
          {itineraryDetailsLabel}
        </BodyText>

        {(!!itinerary || isFlashOffer) && (
          <TextLink weight="regular" size="caption" onClick={openItineraryModal} data-testid="cruise-tile-details-itinerary-modal">
            <CSSBreakpoint min="mobile">{intl.formatMessage({
              id: '0vN6C3AgFjg_q9W9wOHk',
              defaultMessage: 'View itinerary',
              description: 'Link text to open cruise itinerary modal in the cruise section',
            })}
            </CSSBreakpoint>
          </TextLink>
        )}
      </Group>

      <BodyText variant="medium" weight="bold" colour="neutral-two">
        {earliestDepartureDate && !!laterDates.length && <FormattedMessage
          id="sHyA2MM07kZM3tOuXICB"
          defaultMessage="Departing on {departureDate} <moreLink>and more</moreLink>"
          description="Cruise departure date with link to more dates on offer card"
          values={{
            departureDate: earliestDepartureDate,
            moreLink: (chunks) => isFlashOffer
              ? <TextLink>{chunks}</TextLink>
              : <TextLink onClick={openPricesModal} data-testid="cruise-tile-details-prices-modal">{chunks}</TextLink>,
          }}
        />}
        {earliestDepartureDate && !laterDates.length && intl.formatMessage({
          id: 'IccOSRZqxlVvqxUX3Vgt',
          defaultMessage: 'Departing on {departureDate}',
          description: 'Cruise departure date label on offer card in the cruise section',
        }, { departureDate: earliestDepartureDate })}
      </BodyText>
    </Group>
  )
}

export default CruiseDescription
